import { motion } from 'framer-motion';
import { HeartHandshake, Brain, Users, Sparkles, Target, BedDouble, Check, Info } from 'lucide-react';
import Reveal, { easeOut } from './Reveal';
import { scrollToId } from '../lib/scroll';

const IMG_DISABILITY = '/support-disability.jpg';

const SERVICES = [
  {
    id: 'disability-support',
    icon: HeartHandshake,
    title: 'Disability Support',
    tagline: 'Tailored, compassionate everyday assistance',
    desc: 'Personalised care and daily living support that respects individual autonomy, fostering comfort and dignity in every interaction.',
    features: ['Personalised daily routine assistance', 'Mobility & physical support', 'Home care coordination'],
    wide: true,
  },
  {
    id: 'mental-health-support',
    icon: Brain,
    title: 'Mental Health Support',
    tagline: 'Empathetic psychosocial & wellness support',
    desc: 'Dedicated support strategies designed to bolster mental wellbeing, emotional resilience and personal confidence.',
    features: ['Psychosocial recovery support', 'Emotional wellness routines', 'Empathetic peer mentorship'],
  },
  {
    id: 'community-access',
    icon: Users,
    title: 'Community Access',
    tagline: 'Active social connection & regional engagement',
    desc: 'Connecting you to events, activities and sports across Mareeba and the Tablelands to build meaningful relationships.',
    features: ['Social outings & group activities', 'Transport to local hubs', 'Recreational & sports support'],
  },
  {
    id: 'independence-skills',
    icon: Sparkles,
    title: 'Independence Skills',
    tagline: 'Empowering self-reliance for daily living',
    desc: 'Hands-on guidance to help you master new capabilities, from household management to getting out and about with confidence.',
    features: ['Budgeting & meal preparation', 'Public transport confidence', 'Shopping & routine planning'],
  },
  {
    id: 'life-skills-goals',
    icon: Target,
    title: 'Life Skills & Goals',
    tagline: 'Goal setting, education & milestone growth',
    desc: 'Dedicated action planning to turn your personal ambitions into structured, achievable milestones for a fuller life.',
    features: ['NDIS plan goal alignment', 'Volunteering & education prep', 'Personalised milestone mapping'],
  },
  {
    id: 'respite-sta',
    icon: BedDouble,
    title: 'Respite & Short-Term Accommodation',
    tagline: 'Comfortable short stays that refresh everyone',
    desc: 'A safe, welcoming change of scene — quality support during short stays and time out, giving carers a well-earned break with total peace of mind.',
    features: ['Planned short stays (STA)', 'Support during carer breaks', 'Comfortable, welcoming environment'],
    span: 6,
  },
];

function Card({ s, index }) {
  const Icon = s.icon;
  return (
    <motion.article
      data-testid={`support-card-${s.id}`}
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: 0.8, delay: index * 0.08, ease: easeOut }}
      className={`group h-full rounded-3xl bg-white border border-sky-100 shadow-soft hover:shadow-lift hover:-translate-y-1.5 transition-all duration-300 overflow-hidden ${
        { 6: 'lg:col-span-6', 4: 'lg:col-span-4', 2: 'lg:col-span-2' }[s.span || (s.wide ? 4 : 2)]
      }`}
    >
      <div className={`grid h-full ${s.wide ? 'md:grid-cols-2' : ''}`}>
        <div className={`flex flex-col p-7 lg:p-8 ${s.wide ? '' : ''}`}>
          <span className="grid place-items-center h-12 w-12 rounded-2xl bg-gradient-to-br from-sky-500 to-cyan-400 text-white shadow-soft transition-transform duration-300 group-hover:scale-110 group-hover:rotate-6">
            <Icon size={22} />
          </span>
          <h3 className="mt-5 font-display font-bold text-xl lg:text-2xl text-slate-900 tracking-tight">{s.title}</h3>
          <p className="mt-1 text-sm font-semibold text-azure">{s.tagline}</p>
          <p className="mt-3 text-sm lg:text-[15px] text-slate-600 leading-relaxed">{s.desc}</p>
          <ul className={`mt-5 space-y-2.5 ${s.span === 6 && !s.wide ? 'sm:grid sm:grid-cols-3 sm:gap-6 sm:space-y-0' : ''}`}>
            {s.features.map((f) => (
              <li key={f} className="flex items-start gap-2.5 text-sm text-slate-700">
                <span className="mt-0.5 grid place-items-center h-5 w-5 rounded-full bg-cyan-100 text-cyan-700 shrink-0">
                  <Check size={11} strokeWidth={3} />
                </span>
                {f}
              </li>
            ))}
          </ul>
        </div>
        {s.wide && (
          <div className="relative min-h-[240px] md:min-h-0">
            <img
              src={s.img || IMG_DISABILITY}
              alt="Support worker and client enjoying time outdoors"
              className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-gradient-to-tr from-azure/25 to-transparent mix-blend-multiply" />
          </div>
        )}
      </div>
    </motion.article>
  );
}

export default function Supports() {
  return (
    <section id="supports" className="relative bg-brand-cream py-24 lg:py-32">
      <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
        <div className="grid lg:grid-cols-12 gap-8 items-end mb-14">
          <Reveal className="lg:col-span-7">
            <p className="text-xs uppercase tracking-[0.28em] font-semibold text-sky-700">What we do</p>
            <h2 className="mt-4 font-display font-extrabold tracking-tight text-4xl sm:text-5xl text-slate-900 leading-[1.08]">
              Supports built around <span className="font-accent italic font-normal text-azure">you</span>
            </h2>
          </Reveal>
          <Reveal delay={0.15} className="lg:col-span-5">
            <div className="flex items-start gap-3 rounded-2xl border border-sky-100 bg-white p-5 shadow-soft">
              <Info size={18} className="text-azure mt-0.5 shrink-0" />
              <p className="text-sm text-slate-600 leading-relaxed">
                From personal care to <strong className="text-slate-800">respite</strong>,{' '}
                <strong className="text-slate-800">short-term accommodation (STA)</strong> and{' '}
                <strong className="text-slate-800">SIL</strong>, we deliver every NDIS support you
                might need — all from one friendly, local team.
              </p>
            </div>
          </Reveal>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-5">
          {SERVICES.map((s, i) => (
            <Card key={s.id} s={s} index={i} />
          ))}
        </div>

        <Reveal delay={0.1} className="mt-12 text-center">
          <button
            data-testid="support-cta-contact"
            onClick={() => scrollToId('contact')}
            className="rounded-full border-2 border-azure text-azure px-8 py-4 font-display font-bold transition-all hover:bg-azure hover:text-white hover:-translate-y-0.5"
          >
            Talk to us about the support you need
          </button>
        </Reveal>
      </div>
    </section>
  );
}
